import styled from '@emotion/styled';
import { App, Empty, Input, Select, Spin, Switch } from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { API } from '../api';
import { PageHeader } from '../components/common/page-header';
import { useMeSelector } from '../state/me/selectors';
import { TUser } from '../types/responses/users';
import { Paths } from '../types/common';

const ROLE_LABELS: Record<number, string> = {
  0: 'Student',
  1: 'Unauthorized',
  2: 'Teacher',
  3: 'Admin',
};

const adminRoleOptions = [
  { value: 1, label: ROLE_LABELS[1] },
  { value: 0, label: ROLE_LABELS[0] },
  { value: 2, label: ROLE_LABELS[2] },
  { value: 3, label: ROLE_LABELS[3] },
];

const teacherManagerRoleOptions = [
  { value: 1, label: ROLE_LABELS[1] },
  { value: 0, label: ROLE_LABELS[0] },
  { value: 2, label: ROLE_LABELS[2] },
];

const Wrapper = styled('section')`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1180px;
  margin: 0 auto;
`;

const Table = styled('div')`
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  border-radius: 8px;
  background: ${({ theme }) => theme.COLORS.BLACK._400};
`;

const Search = styled(Input.Search)`
  width: min(420px, 100%);
`;

const HeaderRow = styled('div')`
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 130px 190px 160px 140px;
  gap: 16px;
  padding: 14px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  color: ${({ theme }) => theme.COLORS.WHITE._600};
  font-size: 13px;
  font-weight: 600;

  @media (max-width: 760px) {
    display: none;
  }
`;

const Row = styled('div')`
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 130px 190px 160px 140px;
  gap: 16px;
  align-items: center;
  padding: 16px 18px;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const UserInfo = styled('div')`
  min-width: 0;
`;

const Email = styled('div')`
  overflow: hidden;
  color: ${({ theme }) => theme.COLORS.WHITE._100};
  font-size: 15px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Name = styled('div')`
  margin-top: 4px;
  color: ${({ theme }) => theme.COLORS.WHITE._600};
  font-size: 13px;
`;

const RoleText = styled('div')`
  color: ${({ theme }) => theme.COLORS.WHITE._100};
  font-size: 14px;
`;

const Center = styled('div')`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const getUserName = (user: TUser) =>
  [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ');

const canManageAccess = (role?: number, canAssignTeacherRole?: boolean) =>
  role === 3 || !!canAssignTeacherRole;

export const UsersAccessPage: FC = () => {
  const { meData, meLoaded } = useMeSelector();
  const { message } = App.useApp();
  const [users, setUsers] = useState<TUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingPermissionUserId, setPendingPermissionUserId] = useState<
    string | null
  >(null);
  const [pendingStudentUserId, setPendingStudentUserId] = useState<
    string | null
  >(null);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.email.localeCompare(b.email)),
    [users]
  );
  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return sortedUsers;

    return sortedUsers.filter((user) => {
      const fullName = [user.firstName, user.middleName, user.lastName]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase();
      return (
        user.email.toLocaleLowerCase().includes(query) ||
        fullName.includes(query)
      );
    });
  }, [searchQuery, sortedUsers]);
  const isAdmin = meData?.role === 3;
  const roleOptions = isAdmin ? adminRoleOptions : teacherManagerRoleOptions;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.users.getAll({ skip: 0, take: 1000 });
      setUsers(response.data);
    } catch {
      message.error('Users loading failed');
    } finally {
      setLoading(false);
    }
  }, [message]);

  const handleRoleChange = useCallback(
    async (userId: string, role: number) => {
      setPendingUserId(userId);
      try {
        const response = await API.users.updateRole(userId, role);
        setUsers((currentUsers) =>
          currentUsers.map((user) =>
            user.id === userId ? { ...user, role: response.data.role } : user
          )
        );
        message.success('Role updated');
      } catch {
        message.error('Role update failed');
      } finally {
        setPendingUserId(null);
      }
    },
    [message]
  );

  const handlePermissionChange = useCallback(
    async (userId: string, canAssignTeacherRole: boolean) => {
      setPendingPermissionUserId(userId);
      try {
        const response = await API.users.updatePermissions(
          userId,
          { canAssignTeacherRole }
        );
        setUsers((currentUsers) =>
          currentUsers.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  canAssignTeacherRole: response.data.canAssignTeacherRole,
                }
              : user
          )
        );
        message.success('Permission updated');
      } catch {
        message.error('Permission update failed');
      } finally {
        setPendingPermissionUserId(null);
      }
    },
    [message]
  );

  const handleStudentAccessChange = useCallback(
    async (userId: string, studentAccess: boolean) => {
      setPendingStudentUserId(userId);
      try {
        const response = await API.users.updatePermissions(userId, {
          studentAccess,
        });
        setUsers((currentUsers) =>
          currentUsers.map((user) =>
            user.id === userId
              ? { ...user, studentAccess: response.data.studentAccess }
              : user
          )
        );
        message.success('Student access updated');
      } catch {
        message.error('Student access update failed');
      } finally {
        setPendingStudentUserId(null);
      }
    },
    [message]
  );

  useEffect(() => {
    if (canManageAccess(meData?.role, meData?.canAssignTeacherRole)) {
      void loadUsers();
    }
  }, [loadUsers, meData?.canAssignTeacherRole, meData?.role]);

  if (meLoaded && !canManageAccess(meData?.role, meData?.canAssignTeacherRole)) {
    return <Navigate to={Paths.BASE} replace />;
  }

  return (
    <Wrapper>
      <PageHeader>Users & access</PageHeader>

      <Search
        allowClear
        value={searchQuery}
        placeholder="Search by email or name"
        onChange={(event) => setSearchQuery(event.target.value)}
      />

      <Table>
        <HeaderRow>
          <span>User</span>
          <span>Current role</span>
          <span>Set role</span>
          <span>Can assign teachers</span>
          <span>Student access</span>
        </HeaderRow>

        {loading ? (
          <Center>
            <Spin />
          </Center>
        ) : filteredUsers.length ? (
          filteredUsers.map((user) => (
            <Row key={user.id}>
              <UserInfo>
                <Email>{user.email}</Email>
                <Name>{getUserName(user) || 'No name'}</Name>
              </UserInfo>
              <RoleText>{ROLE_LABELS[user.role] ?? user.role}</RoleText>
              <Select
                value={user.role}
                options={roleOptions}
                loading={pendingUserId === user.id}
                disabled={
                  pendingUserId === user.id ||
                  (!isAdmin && (user.role === 3 || user.id === meData?.id))
                }
                onChange={(role) => void handleRoleChange(user.id, role)}
              />
              <Switch
                checked={user.canAssignTeacherRole}
                disabled={!isAdmin || pendingPermissionUserId === user.id}
                loading={pendingPermissionUserId === user.id}
                onChange={(checked) =>
                  void handlePermissionChange(user.id, checked)
                }
              />
              <Switch
                checked={user.studentAccess}
                disabled={!isAdmin || pendingStudentUserId === user.id}
                loading={pendingStudentUserId === user.id}
                onChange={(checked) =>
                  void handleStudentAccessChange(user.id, checked)
                }
              />
            </Row>
          ))
        ) : (
          <Center>
            <Empty />
          </Center>
        )}
      </Table>
    </Wrapper>
  );
};
